import { Injectable, inject } from '@angular/core';
import { ZeroConf } from 'capacitor-zeroconf';
import { DeviceDiscoveryService } from './device-discovery.service';

const ZEROCONF_TYPE = '_http._tcp.local.';
const CENTRAL_HOSTNAME_PREFIX = 'esp32-central-';

/**
 * Uses the native ZeroConf plugin to browse _http._tcp.local services and
 * seed the DeviceDiscoveryService candidate list with any ESP32-Central
 * hostnames found.
 *
 * Only works on Android / iOS (Capacitor native).
 * On web, watch() will throw and the error is swallowed gracefully.
 */
@Injectable({ providedIn: 'root' })
export class ZeroconfDiscoveryService {
    private readonly webDiscovery = inject(DeviceDiscoveryService);

    private watchId: string | null = null;

    /**
     * Browse mDNS for _http._tcp.local services for `timeoutMs` milliseconds,
     * adding any Central hostnames found as probe candidates.
     * Returns the number of new candidates registered.
     */
    async browse(timeoutMs = 4000): Promise<number> {
        let added = 0;

        try {
            await new Promise<void>((resolve) => {
                const timer = setTimeout(resolve, timeoutMs);

                ZeroConf.watch({ type: '_http._tcp.', domain: 'local.' }, (result) => {
                    if (result.action !== 'resolved') {
                        return;
                    }

                    const { hostname, ipv4Addresses } = result.service;
                    const normalised = (hostname ?? '').toLowerCase().replace(/\.local\.?$/, '');

                    if (normalised.startsWith(CENTRAL_HOSTNAME_PREFIX)) {
                        this.webDiscovery.addCandidate(`${normalised}.local`);
                        added += 1;
                        console.log('[ZeroconfDiscovery] Found Central:', normalised, ipv4Addresses);
                    }
                }).then((id) => {
                    this.watchId = id;
                }).catch(() => {
                    clearTimeout(timer);
                    resolve();
                });
            });
        } catch {
            // Not native or permission denied — fail silently.
        } finally {
            if (this.watchId) {
                try {
                    await ZeroConf.unwatch({ type: '_http._tcp.', domain: 'local.' });
                } catch {
                    // ignore
                }
                this.watchId = null;
            }
        }

        return added;
    }
}
